import { useEffect, useMemo, useRef, useState } from "react";
import { motion } from "framer-motion";
import { ArrowRight, Wifi, Smartphone, Sparkles, Tv, Share2, Link2, Minus, Plus, Phone } from "lucide-react";
import { toast } from "sonner";
import { useLang } from "@/context/LanguageContext";
import { api } from "@/lib/api";
import { scrollToId } from "./Nav";

const INTERNET = [
  { id: "livebox", name: "Livebox", speed: "200 Mbps", base: 49, promo: 10, promoDur: 6 },
  { id: "up", name: "Livebox Up", speed: "500 Mbps", base: 58, promo: 10, promoDur: 6 },
  { id: "giga", name: "Livebox Giga", speed: "1 Gbps", base: 68, lifePromo: 15 },
];

const MOBILE = [
  { id: "small", name: "Small", data: "12 GB", base: 15, adv: 3, advMulti: 4 },
  { id: "medium", name: "Medium", data: "70 GB", base: 23, adv: 6, advMulti: 8 },
  { id: "large", name: "Large", data: "140 GB", base: 29, adv: 6.5, advMulti: 9 },
  { id: "unlimited", name: "Unlimited", data: "∞", base: 40, adv: 7, advMulti: 10 },
];

const TV = [
  { id: "none", name: null, base: 0 },
  { id: "lite", name: "TV Lite", base: 10, detail: "20 chaînes (app)" },
  { id: "tv", name: "Orange TV", base: 20, detail: "70 chaînes + décodeur" },
  { id: "plus", name: "Orange TV Plus", base: 32, detail: "70 chaînes + Netflix" },
];

const FIXED_PRICE = 12;

const fmt = (n) => (Number.isInteger(n) ? `${n}` : n.toFixed(2).replace(".", ","));
const clampQty = (v) => Math.min(4, Math.max(0, Number(v) || 0));

const optCls = (active) =>
  `flex-1 min-w-[130px] border px-4 py-3 text-left transition-colors duration-300 ${
    active ? "border-[#FF7900] bg-[#FF7900]/10" : "border-white/15 hover:border-white/40"
  }`;

export const PackCalculator = () => {
  const { t } = useLang();
  const c = t.calculator;
  const qp = new URLSearchParams(window.location.search);
  const [net, setNet] = useState(() => (INTERNET.some((x) => x.id === qp.get("net")) ? qp.get("net") : "giga"));
  const [tv, setTv] = useState(() => (TV.some((x) => x.id === qp.get("tv")) ? qp.get("tv") : "none"));
  const [fixe, setFixe] = useState(() => qp.get("fix") === "1");
  const [counts, setCounts] = useState(() => {
    const init = { small: clampQty(qp.get("s")), medium: clampQty(qp.get("m")), large: clampQty(qp.get("l")), unlimited: clampQty(qp.get("u")) };
    return Object.values(init).some((v) => v > 0) || qp.has("net") ? init : { small: 0, medium: 1, large: 0, unlimited: 0 };
  });

  useEffect(() => {
    if (new URLSearchParams(window.location.search).has("net")) {
      setTimeout(() => scrollToId("calculator"), 800);
    }
  }, []);

  const bump = (id, d) => setCounts((cs) => ({ ...cs, [id]: clampQty(cs[id] + d) }));

  const r = useMemo(() => {
    const i = INTERNET.find((x) => x.id === net);
    const tvOpt = TV.find((x) => x.id === tv);
    const total = MOBILE.reduce((s, m) => s + counts[m.id], 0);
    const mlu = counts.medium + counts.large + counts.unlimited;
    const base = i.base + MOBILE.reduce((s, m) => s + m.base * counts[m.id], 0) + tvOpt.base + (fixe ? FIXED_PRICE : 0);
    const adv = total >= 1 ? MOBILE.reduce((s, m) => s + (total >= 2 ? m.advMulti : m.adv) * counts[m.id], 0) : 0;
    const netLifetime = i.lifePromo || 0;
    const lifetime = netLifetime;
    const temps = [];
    if (i.promo) temps.push({ a: i.promo, d: i.promoDur });
    if (tv === "plus") temps.push({ a: 12, d: 12 });
    const temp = temps.reduce((s, x) => s + x.a, 0);
    const durs = [...new Set(temps.map((x) => x.d))];
    const permanent = base - adv - lifetime;
    return { i, tvOpt, total, base, adv, lifetime, temp, tempDur: durs.length === 1 ? durs[0] : null, permanent, promo: permanent - temp };
  }, [net, tv, counts, fixe]);

  const comboLabel = [
    r.i.name,
    ...MOBILE.filter((m) => counts[m.id] > 0).map((m) => (counts[m.id] > 1 ? `${m.name} ×${counts[m.id]}` : m.name)),
    r.tvOpt.name,
    fixe ? c.fixedName : null,
  ]
    .filter(Boolean)
    .join(" + ");

  const mounted = useRef(false);
  useEffect(() => {
    if (!mounted.current) {
      mounted.current = true;
      return;
    }
    const id = setTimeout(() => api.post("/simulations", { label: comboLabel }).catch(() => {}), 1500);
    return () => clearTimeout(id);
  }, [comboLabel]);

  const shareUrl = `${window.location.origin}/?net=${net}&s=${counts.small}&m=${counts.medium}&l=${counts.large}&u=${counts.unlimited}&tv=${tv}&fix=${fixe ? 1 : 0}`;
  const shareMsg = `${c.shareText} ${comboLabel} — €${fmt(r.promo)}${c.perMonth}${r.temp > 0 ? "" : ` ${c.forLifeShort}`}. ${shareUrl}`;

  const copyShare = async () => {
    await navigator.clipboard.writeText(shareUrl);
    toast.success(c.linkCopied);
  };

  const attachSim = () => {
    const label = `${comboLabel} — €${fmt(r.promo)}${c.perMonth}${
      r.temp > 0 ? ` (${r.tempDur ? `${r.tempDur} ${c.months}` : c.promoPeriod}), ${c.then} €${fmt(r.permanent)}${c.perMonth}` : ` ${c.forLifeShort}`
    }`;
    api.post("/simulations", { label: comboLabel, cta: true }).catch(() => {});
    sessionStorage.setItem("sta_sim", label);
    window.dispatchEvent(new CustomEvent("sta-sim", { detail: label }));
    scrollToId("contact");
  };

  return (
    <section id="calculator" className="py-24 md:py-32 border-t border-white/10 bg-[#0d0d0d]" data-testid="pack-calculator">
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.6 }}
          className="text-xs md:text-sm uppercase tracking-[0.25em] text-[#FF7900] mb-6"
        >
          {c.overline}
        </motion.p>
        <motion.h2
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7, delay: 0.05 }}
          className="font-display text-4xl md:text-5xl font-medium tracking-tight max-w-3xl mb-4"
        >
          {c.title}
        </motion.h2>
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7, delay: 0.1 }}
          className="text-white/60 max-w-2xl mb-12"
        >
          {c.sub}
        </motion.p>

        <div className="grid lg:grid-cols-[1fr_380px] gap-8 items-start">
          <div className="space-y-8">
            <div>
              <p className="flex items-center gap-2 text-xs uppercase tracking-widest text-white/40 mb-4">
                <Wifi className="w-4 h-4 text-[#FF7900]" /> {c.internet}
              </p>
              <div className="flex flex-wrap gap-3">
                {INTERNET.map((o) => (
                  <button key={o.id} onClick={() => setNet(o.id)} className={optCls(net === o.id)} data-testid={`calc-internet-${o.id}`}>
                    <span className="block font-semibold text-sm">{o.name}</span>
                    <span className="block text-xs text-white/45 mt-1">{o.speed} · €{o.base}{c.perMonth}</span>
                  </button>
                ))}
              </div>
            </div>
            <div>
              <p className="flex items-center gap-2 text-xs uppercase tracking-widest text-white/40 mb-4">
                <Smartphone className="w-4 h-4 text-[#FF7900]" /> {c.mobile} <span className="normal-case tracking-normal text-white/30">— {c.mixHint}</span>
              </p>
              <div className="grid sm:grid-cols-2 gap-3">
                {MOBILE.map((m) => (
                  <div
                    key={m.id}
                    className={`flex items-center justify-between border px-4 py-3 transition-colors duration-300 ${
                      counts[m.id] > 0 ? "border-[#FF7900] bg-[#FF7900]/10" : "border-white/15"
                    }`}
                    data-testid={`calc-mobile-${m.id}`}
                  >
                    <div>
                      <span className="block font-semibold text-sm">{m.name}</span>
                      <span className="block text-xs text-white/45 mt-0.5">{m.data} · €{m.base}{c.perMonth}</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <button
                        onClick={() => bump(m.id, -1)}
                        data-testid={`calc-mobile-${m.id}-minus`}
                        className="w-8 h-8 border border-white/20 rounded-full flex items-center justify-center text-white/60 hover:border-[#FF7900] hover:text-[#FF7900] transition-colors duration-300"
                      >
                        <Minus className="w-3.5 h-3.5" />
                      </button>
                      <span className="w-4 text-center font-semibold text-sm" data-testid={`calc-mobile-${m.id}-qty`}>
                        {counts[m.id]}
                      </span>
                      <button
                        onClick={() => bump(m.id, 1)}
                        data-testid={`calc-mobile-${m.id}-plus`}
                        className="w-8 h-8 border border-white/20 rounded-full flex items-center justify-center text-white/60 hover:border-[#FF7900] hover:text-[#FF7900] transition-colors duration-300"
                      >
                        <Plus className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
              {r.total >= 2 && <p className="text-xs text-emerald-400 mt-3">{c.multiCardNote}</p>}            </div>
            <div>
              <p className="flex items-center gap-2 text-xs uppercase tracking-widest text-white/40 mb-4">
                <Tv className="w-4 h-4 text-[#FF7900]" /> {c.tv}
              </p>
              <div className="flex flex-wrap gap-3">
                {TV.map((o) => (
                  <button key={o.id} onClick={() => setTv(o.id)} className={optCls(tv === o.id)} data-testid={`calc-tv-${o.id}`}>
                    <span className="block font-semibold text-sm">{o.name || c.tvNone}</span>
                    <span className="block text-xs text-white/45 mt-1">{o.name ? `${o.detail} · €${o.base}${c.perMonth}` : c.tvNoneDetail}</span>
                  </button>
                ))}
              </div>
            </div>
            <div>
              <p className="flex items-center gap-2 text-xs uppercase tracking-widest text-white/40 mb-4">
                <Phone className="w-4 h-4 text-[#FF7900]" /> {c.fixed}
              </p>
              <div className="flex flex-wrap gap-3">
                <button onClick={() => setFixe(false)} className={optCls(!fixe)} data-testid="calc-fixed-none">
                  <span className="block font-semibold text-sm">{c.fixedNone}</span>
                  <span className="block text-xs text-white/45 mt-1">€0</span>
                </button>
                <button onClick={() => setFixe(true)} className={optCls(fixe)} data-testid="calc-fixed-yes">
                  <span className="block font-semibold text-sm">{c.fixedName}</span>
                  <span className="block text-xs text-white/45 mt-1">{c.fixedDetail} · €{FIXED_PRICE}{c.perMonth}</span>
                </button>
              </div>
            </div>
            <p className="text-xs text-white/35 max-w-lg">{c.note}</p>
          </div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
            className="border border-[#FF7900]/40 bg-[#111111] p-8"
            data-testid="calc-result"
          >
            <p className="text-xs text-white/40 mb-4" data-testid="calc-combo-label">{comboLabel}</p>
            <div className="space-y-2.5 text-sm border-b border-white/10 pb-5 mb-5">
              <div className="flex justify-between text-white/55">
                <span>{c.base}</span>
                <span>€{fmt(r.base)}{c.perMonth}</span>
              </div>
              {r.adv > 0 && (
                <div className="flex justify-between text-white/55">
                  <span>{c.advantage}</span>
                  <span>−€{fmt(r.adv)}</span>
                </div>
              )}
              {r.lifetime > 0 && (
                <div className="flex justify-between text-[#FF7900] font-semibold">
                  <span>{c.lifetime}</span>
                  <span>−€{fmt(r.lifetime)}</span>
                </div>
              )}
              {r.temp > 0 && (
                <div className="flex justify-between text-emerald-400">
                  <span>{c.temp} ({r.tempDur ? `${r.tempDur} ${c.months}` : c.promoPeriod})</span>
                  <span>−€{fmt(r.temp)}</span>
                </div>
              )}
            </div>
            <p className="font-display text-5xl font-medium text-[#FF7900]" data-testid="calc-result-price">
              €{fmt(r.promo)}
              <span className="text-lg text-white/50 font-normal">{c.perMonth}</span>
            </p>
            <p className="text-sm text-white/60 mt-2" data-testid="calc-result-detail">
              {r.temp > 0
                ? `${c.during} ${r.tempDur ? `${r.tempDur} ${c.months}` : c.promoPeriod}, ${c.then} €${fmt(r.permanent)}${c.perMonth}`
                : c.forLife}
            </p>
            {r.base - r.promo > 0 && (
              <p className="flex items-center gap-2 text-xs text-emerald-400 mt-4">
                <Sparkles className="w-3.5 h-3.5" />
                {c.save} €{fmt(r.base - r.promo)}{c.perMonth}
              </p>
            )}
            {r.lifetime > 0 && r.base - r.permanent > 0 && (
              <div className="mt-5 border border-emerald-500/30 bg-emerald-500/5 p-4" data-testid="calc-compare-card">
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <p className="text-[11px] uppercase tracking-widest text-white/40 mb-1">{c.compareCatalog}</p>
                    <p className="font-display text-2xl text-white/40 line-through" data-testid="calc-compare-before">€{fmt(r.base)}{c.perMonth}</p>
                  </div>
                  <ArrowRight className="w-5 h-5 text-emerald-400 shrink-0" />
                  <div className="text-right">
                    <p className="text-[11px] uppercase tracking-widest text-emerald-400 mb-1">{c.compareDeal}</p>
                    <p className="font-display text-2xl font-semibold text-emerald-400" data-testid="calc-compare-after">€{fmt(r.permanent)}{c.perMonth}</p>
                  </div>
                </div>
                <p className="mt-3 text-xs font-semibold text-emerald-400 text-center" data-testid="calc-compare-yearly">
                  {c.compareSave.replace("{y}", fmt((r.base - r.permanent) * 12))}
                </p>
              </div>
            )}
            <button
              onClick={attachSim}
              data-testid="calc-cta"
              className="mt-6 w-full inline-flex items-center justify-center gap-2 bg-[#FF7900] hover:bg-white hover:text-black text-white text-sm font-semibold px-6 py-3 rounded-full transition-colors duration-300"
            >
              {c.cta}
              <ArrowRight className="w-4 h-4" />
            </button>
            <div className="mt-3 flex items-center gap-2">
              <a
                href={`https://wa.me/?text=${encodeURIComponent(shareMsg)}`}
                target="_blank"
                rel="noreferrer"
                data-testid="calc-share-whatsapp"
                className="flex-1 inline-flex items-center justify-center gap-2 border border-[#25D366]/50 text-[#25D366] hover:bg-[#25D366] hover:text-white text-xs font-semibold px-4 py-2.5 rounded-full transition-colors duration-300"
              >
                <Share2 className="w-3.5 h-3.5" /> {c.share}
              </a>
              <button
                onClick={copyShare}
                data-testid="calc-share-copy"
                className="flex-1 inline-flex items-center justify-center gap-2 border border-white/20 text-white/60 hover:border-[#FF7900] hover:text-[#FF7900] text-xs font-semibold px-4 py-2.5 rounded-full transition-colors duration-300"
              >
                <Link2 className="w-3.5 h-3.5" /> {c.copyLink}
              </button>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
